/**
 * Entrada al portal del paciente.
 *
 * Un solo campo para identificarse: acepta el DNI o el correo. Poner dos
 * campos —"¿entras con documento o con correo?"— es hacerle elegir al paciente
 * algo que el sistema puede deducir mirando si hay una arroba.
 *
 * El error nunca dice cuál de los dos falló. "Ese DNI no existe" le confirma a
 * cualquiera qué documentos están registrados en la clínica.
 */
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogIn } from "lucide-react";
import { usePortalAuth } from "../../auth/PortalAuthProvider";
import { useTenantBranding, useBase } from "../../auth/TenantBrandingProvider";
import { formatApiError } from "../../api/client";
import { Alert, Button, Field, Input } from "../../components/ui";

export function PortalLoginPage() {
  // Con dominio propio no hay slug en la URL y la base es "": los enlaces
  // del portal quedan `/portal/citas` en vez de `/mi-salud/portal/citas`.
  const base = useBase();
  const { estado, entrar } = usePortalAuth();
  const { branding } = useTenantBranding();
  // El slug de verdad, para lo que SÍ lo necesita: el login del portal lo manda
  // a la API y el logo se pide por él. Sale del branding, que ya resolvió el
  // policlínico venga de la URL o del dominio.
  const slug = branding?.slug ?? "";
  const navigate = useNavigate();
  const [identificador, setIdentificador] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (estado === "dentro") return <Navigate to={`${base}/portal`} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await entrar(slug, identificador.trim(), password);
      navigate(`${base}/portal`, { replace: true });
    } catch (err) {
      setError(formatApiError(err, "No pudimos entrar. Revisa tus datos."));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          {branding?.has_logo && (
            <img
              src={`/api/v1/tenants/by-slug/${slug}/logo`}
              alt=""
              className="mx-auto mb-3 h-14 w-14 rounded-xl object-contain"
            />
          )}
          <h1 className="text-xl font-bold text-slate-900">
            {branding?.nombre_comercial ?? "Portal del paciente"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Consulta tus citas, recetas y resultados.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          {error && <Alert>{error}</Alert>}

          <Field
            label="DNI o correo"
            htmlFor="p_id"
            hint="El mismo documento con el que te atiendes."
          >
            <Input
              id="p_id"
              value={identificador}
              onChange={(e) => setIdentificador(e.target.value)}
              autoComplete="username"
              autoFocus
              placeholder="70125896"
            />
          </Field>

          <Field label="Contraseña" htmlFor="p_pw">
            <Input
              id="p_pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>

          <Button
            type="submit"
            className="w-full"
            loading={enviando}
            disabled={!identificador.trim() || !password}
          >
            <LogIn className="h-4 w-4" />
            Entrar
          </Button>

          {/* El mostrador deja de ser la única salida. Sigue nombrándose
              porque hay pacientes sin correo registrado, y para ellos es la
              única — pero primero se ofrece lo que puede resolver solo. */}
          <p className="text-center text-xs text-slate-500">
            <Link
              to={`${base}/recuperar`}
              className="font-medium text-[color:var(--color-link)] hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </Link>
            <br />
            Si no tienes correo registrado, pídela en admisión con tu documento.
          </p>
        </form>
      </div>
    </div>
  );
}
